import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

/** Biriktirma yopiq saqlanadi — havola serverda tekshirilib, qisqa muddatga imzolanadi. */
export async function getTicketAttachmentUrl(ticketId: number, messageId: number, admin = false): Promise<string | null> {
  const path = admin
    ? '/admin/tickets/{ticket}/messages/{message}/attachment-url' as const
    : '/tickets/{ticket}/messages/{message}/attachment-url' as const;
  const { data, error } = await apiClient.GET(path, {
    params: { path: { ticket: ticketId, message: messageId } },
  });
  if (error) return null;
  return (data as { data?: { url?: string } })?.data?.url ?? null;
}

function isImageName(value: string) {
  return /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(value);
}

async function download(url: string, name: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name || 'file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank');
  }
}

interface Props {
  ticketId: number;
  messageId: number;
  name: string;
  admin?: boolean;
  className?: string;
}

export function TicketAttachment({ ticketId, messageId, name, admin = false, className = '' }: Props) {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const showImage = isImageName(name);

  useEffect(() => {
    if (!showImage) return;
    let alive = true;
    setLoading(true);
    getTicketAttachmentUrl(ticketId, messageId, admin).then((url) => {
      if (alive) {
        setSignedUrl(url);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [ticketId, messageId, admin, showImage]);

  return (
    <div className={`mt-1 space-y-1 ${className}`}>
      {showImage && loading && <Loader2 size={14} className="animate-spin opacity-60" />}
      {showImage && signedUrl && (
        <a href={signedUrl} target="_blank" rel="noopener noreferrer" className="block">
          <img src={signedUrl} alt={name} className="max-w-[200px] max-h-[150px] rounded border object-cover" />
        </a>
      )}
      <button
        type="button"
        onClick={async () => {
          const url = signedUrl ?? (await getTicketAttachmentUrl(ticketId, messageId, admin));
          if (url) await download(url, name);
        }}
        className="flex items-center gap-1.5 text-xs underline cursor-pointer hover:opacity-80"
      >
        <FileText size={14} /> {name || t('tick_attach.file')} · {t('tick_attach.download')}
      </button>
    </div>
  );
}
